import styled, { keyframes, css } from 'styled-components';

import { COLORS, ACQ, MOBILE, ANIM, GRADIENTS } from '../../../../theme/tokens';
import { scrollFadeMaskY } from '../../../../theme/mixins';
import { Panel, Text } from '../../../../components';

// Shared 5-column track for the table's left block (Operation / Category / Scope
// / Channel / Time); the Status cell sits to its right in its own fixed column.
const MAIN_COLS = '1.7fr 1fr 1.35fr 1fr 1.05fr';
const STATUS_W = '128px';

// The feed holds a full page of the tenant's stream (100 events), so it scrolls
// inside its own panel rather than stretching the page: roughly eight rows tall,
// the height the feed already occupied when it only fetched seven.
const FEED_MAX_H = '424px';
const FEED_MAX_H_MOBILE = '560px';

/** Width of the feed's own thin scrollbar. */
const SCROLLBAR_W = '4px';

// Same opaque Figma surface as the stats panel above it (#18181b under a 0.32
// black overlay, GRADIENTS.featureCard), overriding Panel's translucent default
// so the feed container reads solid rather than see-through over the page.
export const FeedPanel = styled(Panel)`
  background: ${GRADIENTS.featureCard};
`;

// --- Live heading ------------------------------------------------------------
// The feed titles itself, top-left: "Live" with a pulsing dot beside it. The dot
// is the whole live cue, so it carries no label of its own.
export const FeedHead = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 4px 14px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.6); }
`;

// The heading renders as an <h3>, which the app-wide Bootstrap stylesheet gives a
// bottom margin and a loose line-height. Both are cleared here: the row centres
// its items on this box, so any space the title carries below its glyphs pushes
// the dot down and the pair stops reading as one unit. `line-height: 1` brings the
// box down onto the type, leaving the dot on the word's own optical centre.
export const LiveTitle = styled(Text)`
  margin: 0;
  font-size: 20px;
  line-height: 1;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const LiveDot = styled.i`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${ACQ.live};
  animation: ${pulse} ${ANIM.FEED_PULSE_MS}ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// --- Column header (desktop only) -------------------------------------------
// Sits OUTSIDE the scroll container. On a platform with classic (space-taking)
// scrollbars the rows' box is therefore narrower than this one by the scrollbar
// width, drifting the columns a few px. Left alone deliberately: the reserved
// width is engine-dependent (4px here via the webkit rule, ~11px for Firefox's
// `thin`, and ZERO wherever scrollbars overlay, as on macOS), so any fixed
// compensation fixes one platform by breaking another. The robust fix is to move
// this row inside the scroller, which the edge mask currently rules out.
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 12px;
  border-bottom: 1px solid ${COLORS.hairline};

  ${MOBILE} {
    display: none;
  }
`;

export const HeaderMain = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: grid;
  grid-template-columns: ${MAIN_COLS};
  gap: 12px;
  padding-left: 12px;
`;

export const HeaderCell = styled(Text)`
  font-size: 14px;
  color: ${COLORS.textMuted};
`;

export const HeaderStatus = styled(Text)`
  flex: 0 0 ${STATUS_W};
  font-size: 14px;
  color: ${COLORS.textMuted};
`;

// --- Rows --------------------------------------------------------------------
// The scroll container for the stream: ONE continuous, time-descending list. It
// opens parked on the newest operation that has already run, so scrolling up
// walks into what is still scheduled and scrolling down into what already
// happened; the edges fade only while there is more feed past them.
//
// `position: relative` makes it the offset parent of its rows, so the opening
// scroll position can be computed from a row's `offsetTop` (a layout value the
// rows' slide-in transform cannot skew).
//
// Scroll chaining is deliberately LEFT ON: on mobile the document is the page
// scroller and this panel takes most of the viewport, so `overscroll-behavior:
// contain` would trap a swipe that starts anywhere on the feed and leave the
// reader unable to scroll the page past it. Containment is right for the area's
// overlays and sheets, not for an in-page region. The two-value overflow clips
// the x-axis so the y scrollbar can never imply a horizontal one.
export const FeedList = styled.div<{ $fadeTop: boolean; $fadeBottom: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: ${FEED_MAX_H};
  overflow: hidden auto;
  scrollbar-width: thin;
  scrollbar-color: ${COLORS.hairline} transparent;
  ${(p) => scrollFadeMaskY(p.$fadeTop, p.$fadeBottom)}

  &::-webkit-scrollbar {
    width: ${SCROLLBAR_W};
  }
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.hairline};
    border-radius: 9999px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  ${MOBILE} {
    max-height: ${FEED_MAX_H_MOBILE};
  }
`;

const rowIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// Newly-pushed rows briefly flash a green wash that fades to transparent, marking
// what the last poll brought in. New operations arrive at the TOP of the stream,
// which is above where the feed parks itself, so the flash is what the reader
// sees when they scroll up into the scheduled half rather than a live cue at rest.
const flash = keyframes`
  0% { background: ${ACQ.successBg}; }
  100% { background: transparent; }
`;

export const Row = styled.div<{ $fresh: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-bottom: 1px solid ${COLORS.hairline};
  border-radius: 8px;
  /* Slide every row in on mount; the green flash plays only for rows a poll
     brought in (not the seed rows already on the page at load). */
  animation: ${(p) =>
    p.$fresh
      ? css`
          ${rowIn} ${ANIM.FEED_ROW_IN_MS}ms ${ANIM.EASE} both, ${flash} ${ANIM.FEED_FLASH_MS}ms ease both
        `
      : css`
          ${rowIn} ${ANIM.FEED_ROW_IN_MS}ms ${ANIM.EASE} both
        `};

  &:last-child {
    border-bottom: 0;
  }

  ${MOBILE} {
    align-items: flex-start;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const RowMain = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: grid;
  grid-template-columns: ${MAIN_COLS};
  align-items: center;
  gap: 12px;
  padding-left: 12px;

  ${MOBILE} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding-left: 0;
  }
`;

export const OpName = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.heading};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    white-space: normal;
  }
`;

export const Cell = styled(Text)`
  font-size: 14px;
  color: ${COLORS.textMuted};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    display: none;
  }
`;

// When the event landed (or is due). Tabular figures keep the column aligned
// down the stream, which is most of what makes it read like a feed rather than
// a list.
export const TimeCell = styled(Cell)`
  font-variant-numeric: tabular-nums;
`;

// The same timestamp on mobile, where the row stacks and there is no Time
// column: it leads the meta block, right under the operation name.
export const MobileTime = styled(Text)`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
`;

// The Category / Scope / Channel lines, shown only when the row stacks (mobile).
export const MobileMeta = styled.div`
  display: none;

  ${MOBILE} {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 2px;
  }
`;

export const MetaLine = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

export const StatusCell = styled.div`
  flex: 0 0 ${STATUS_W};
  display: flex;
  align-items: center;
  gap: 8px;

  ${MOBILE} {
    flex: 0 0 auto;
    align-self: flex-start;
  }
`;

export const StatusText = styled(Text)<{ $color: string }>`
  font-size: 14px;
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

// --- Loading skeleton --------------------------------------------------------
// A non-flashing stand-in for a feed row while the first page loads; same
// hairline-ruled layout as `Row` so the table doesn't jump on reveal.
export const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-bottom: 1px solid ${COLORS.hairline};

  &:last-child {
    border-bottom: 0;
  }

  ${MOBILE} {
    align-items: flex-start;
  }
`;

export const SkeletonStatus = styled.div`
  flex: 0 0 ${STATUS_W};

  ${MOBILE} {
    flex: 0 0 auto;
  }
`;
